import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { releaseFocusBeforeOverlay } from '../../../shared/lib/utils/accessibilityUtils';
import {
  clearQuickFillTourProgress,
  hasSeenQuickFillOnboarding,
  markQuickFillOnboardingSeen,
  readQuickFillTourProgress,
  writeQuickFillTourProgress,
} from '../lib/quickFillOnboarding';
import {
  PREVIEW_AUTO_FILL_DIALOG_STATE,
} from '../ui/quickFillTourState';
import type {
  QuickFillAutoFillPreview,
  QuickFillAutoFillPreviewMode,
} from '../ui/components/QuickFillAutoFillPreviewDialog';

const EMPTY_AUTO_FILL_PREVIEW: QuickFillAutoFillPreview = {
  matches: 0,
  total: 0,
  preview: '',
};

export interface ParameterChangeDialogState {
  open: boolean;
  propertyId: string;
  oldParameter: string;
  newParameter: string;
}

export interface AutoFillDialogState {
  open: boolean;
  mode: QuickFillAutoFillPreviewMode;
  preview: QuickFillAutoFillPreview;
}

export interface UseQuickFillDialogsOptions {
  /**
   * Returns a preview for the requested auto-fill mode. When the preview is
   * truthy the dialog is opened; when it is null/undefined the dialog stays
   * closed.
   */
  handleAutoFillPreview: (
    mode: QuickFillAutoFillPreviewMode
  ) => QuickFillAutoFillPreview | null | undefined;

  /** Applies the auto-fill changes for the requested mode. */
  handleConfirmAutoFill: (mode: QuickFillAutoFillPreviewMode) => void;

  /** Updates the Revit parameter bound to a property. */
  updatePropertyRevitParameter: (propertyId: string, revitParameter: string) => void;

  /**
   * Tour step definitions. The hook derives the active step id and step count
   * from this array, so callers do not need to compute the current step id
   * from the returned tourStep.
   */
  tourSteps: Array<{ id: string }>;

  /** Optional callback invoked before the tour opens (e.g. to snapshot panel state). */
  onBeforeOpenTour?: () => void;
}

export interface UseQuickFillDialogsResult {
  // Raw dialog states
  parameterChangeDialog: ParameterChangeDialogState;
  setParameterChangeDialog: (value: ParameterChangeDialogState) => void;
  autoFillDialog: AutoFillDialogState;
  findReplaceOpen: boolean;
  setFindReplaceOpen: (value: boolean) => void;
  csvImportOpen: boolean;
  setCSVImportOpen: (value: boolean) => void;
  guideOpen: boolean;
  setGuideOpen: (value: boolean) => void;
  tourOpen: boolean;
  tourStep: number;
  setTourStep: React.Dispatch<React.SetStateAction<number>>;
  tourResumeStep: number | null;

  // Parameter change dialog handlers
  handleConfirmParameterChange: () => void;
  handleCancelParameterChange: () => void;

  // Auto-fill dialog handlers
  handleAutoFillPreviewDialog: (mode: QuickFillAutoFillPreviewMode) => void;
  handleConfirmAutoFillDialog: () => void;
  handleCancelAutoFill: () => void;

  // Tour handlers
  handleOpenTour: (options?: { restart?: boolean }) => void;
  handleCloseTour: () => void;
  handleRestartTour: () => void;
  refreshTourResumeStep: () => void;
  openGuide: () => void;

  // Tour runtime state
  currentTourStepId: string | null;
  runtimeIntent: {
    forceNavigatorPreview: boolean;
    showSaveReviewPreview: boolean;
    openValuePanel: boolean;
  };

  // Effective open states (including tour preview-only overrides)
  effectiveAutoFillDialogOpen: boolean;
  autoFillPreviewOnly: boolean;
  effectiveAutoFillDialogMode: QuickFillAutoFillPreviewMode;
  effectiveAutoFillDialogPreview: QuickFillAutoFillPreview;
  effectiveFindReplaceOpen: boolean;
  findReplacePreviewOnly: boolean;
}

const INITIAL_PARAMETER_CHANGE_DIALOG: ParameterChangeDialogState = {
  open: false,
  propertyId: '',
  oldParameter: '',
  newParameter: '',
};

const INITIAL_AUTO_FILL_DIALOG: AutoFillDialogState = {
  open: false,
  mode: 'all',
  preview: EMPTY_AUTO_FILL_PREVIEW,
};

/**
 * Manages dialog open/close state and tour preview-only overrides for the
 * Quick Fill workspace. Keeps the real dialog states separate from the
 * synthetic preview states shown during the spotlight tour.
 */
export function useQuickFillDialogs(
  options: UseQuickFillDialogsOptions
): UseQuickFillDialogsResult {
  const {
    handleAutoFillPreview,
    handleConfirmAutoFill,
    updatePropertyRevitParameter,
    tourSteps,
    onBeforeOpenTour,
  } = options;

  const [parameterChangeDialog, setParameterChangeDialog] = useState<ParameterChangeDialogState>(
    INITIAL_PARAMETER_CHANGE_DIALOG
  );
  const [autoFillDialog, setAutoFillDialog] = useState<AutoFillDialogState>(INITIAL_AUTO_FILL_DIALOG);
  const [findReplaceOpen, setFindReplaceOpen] = useState(false);
  const [csvImportOpen, setCSVImportOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourResumeStep, setTourResumeStep] = useState<number | null>(null);

  const handleConfirmParameterChange = useCallback(() => {
    const { propertyId, newParameter } = parameterChangeDialog;
    updatePropertyRevitParameter(propertyId, newParameter);
    setParameterChangeDialog(INITIAL_PARAMETER_CHANGE_DIALOG);
  }, [parameterChangeDialog, updatePropertyRevitParameter]);

  const handleCancelParameterChange = useCallback(() => {
    setParameterChangeDialog(INITIAL_PARAMETER_CHANGE_DIALOG);
  }, []);

  const handleAutoFillPreviewDialog = useCallback(
    (mode: QuickFillAutoFillPreviewMode) => {
      const preview = handleAutoFillPreview(mode);
      if (preview) {
        releaseFocusBeforeOverlay();
        setAutoFillDialog({ open: true, mode, preview });
      }
    },
    [handleAutoFillPreview]
  );

  const handleConfirmAutoFillDialog = useCallback(() => {
    handleConfirmAutoFill(autoFillDialog.mode);
    setAutoFillDialog(INITIAL_AUTO_FILL_DIALOG);
  }, [autoFillDialog.mode, handleConfirmAutoFill]);

  const handleCancelAutoFill = useCallback(() => {
    setAutoFillDialog(INITIAL_AUTO_FILL_DIALOG);
  }, []);

  const refreshTourResumeStep = useCallback(() => {
    setTourResumeStep(readQuickFillTourProgress(tourSteps.length));
  }, [tourSteps]);

  const handleCloseTour = useCallback(() => {
    if (tourOpen) {
      writeQuickFillTourProgress(tourStep, tourSteps.length);
      setTourResumeStep(readQuickFillTourProgress(tourSteps.length));
    }

    setTourOpen(false);
    setTourStep(0);
  }, [tourOpen, tourStep, tourSteps]);

  const handleOpenTour = useCallback(
    (tourOptions?: { restart?: boolean }) => {
      const nextTourStep = tourOptions?.restart
        ? 0
        : readQuickFillTourProgress(tourSteps.length) ?? 0;

      if (tourOptions?.restart) {
        clearQuickFillTourProgress();
      }

      onBeforeOpenTour?.();
      setGuideOpen(false);
      setTourStep(nextTourStep);
      setTourOpen(true);
      setTourResumeStep(null);

      if (!hasSeenQuickFillOnboarding()) {
        markQuickFillOnboardingSeen();
      }
    },
    [onBeforeOpenTour, tourSteps]
  );

  const handleRestartTour = useCallback(() => {
    handleOpenTour({ restart: true });
  }, [handleOpenTour]);

  const openGuide = useCallback(() => {
    handleCloseTour();
    refreshTourResumeStep();
    setGuideOpen(true);
  }, [handleCloseTour, refreshTourResumeStep]);

  // Persist tour progress while the tour is running.
  useEffect(() => {
    if (!tourOpen) {
      return;
    }

    writeQuickFillTourProgress(tourStep, tourSteps.length);
    setTourResumeStep(null);
  }, [tourOpen, tourStep, tourSteps]);

  // Initialize the resume step on mount.
  useEffect(() => {
    refreshTourResumeStep();
  }, [refreshTourResumeStep]);

  // Effective open states for tour preview-only mode.
  const currentTourStepId = tourOpen ? (tourSteps[tourStep]?.id ?? null) : null;
  const showTourAutoFillPreview =
    currentTourStepId === 'auto-fill-preview' && !autoFillDialog.open;
  // The real save-review dialog is owned by useQuickFillSubmit; this hook only
  // needs the tour-step flag for the preview override.
  const showTourSaveReviewPreview = currentTourStepId === 'save-review';
  const showTourFindReplacePreview =
    currentTourStepId === 'find-replace-preview' && !findReplaceOpen;

  const effectiveAutoFillDialogOpen = autoFillDialog.open || showTourAutoFillPreview;
  const autoFillPreviewOnly = showTourAutoFillPreview;
  const effectiveAutoFillDialogMode = showTourAutoFillPreview
    ? PREVIEW_AUTO_FILL_DIALOG_STATE.mode
    : autoFillDialog.mode;
  const effectiveAutoFillDialogPreview = showTourAutoFillPreview
    ? PREVIEW_AUTO_FILL_DIALOG_STATE.preview
    : autoFillDialog.preview;

  const effectiveFindReplaceOpen = findReplaceOpen || showTourFindReplacePreview;
  const findReplacePreviewOnly = showTourFindReplacePreview;

  const runtimeIntent = useMemo(
    () => ({
      forceNavigatorPreview: currentTourStepId === 'navigator',
      showSaveReviewPreview: showTourSaveReviewPreview,
      openValuePanel: currentTourStepId === 'value-panel' || currentTourStepId === 'value-search',
    }),
    [currentTourStepId, showTourSaveReviewPreview]
  );

  return useMemo(
    () => ({
      parameterChangeDialog,
      setParameterChangeDialog,
      autoFillDialog,
      findReplaceOpen,
      setFindReplaceOpen,
      csvImportOpen,
      setCSVImportOpen,
      guideOpen,
      setGuideOpen,
      tourOpen,
      tourStep,
      setTourStep,
      tourResumeStep,
      handleConfirmParameterChange,
      handleCancelParameterChange,
      handleAutoFillPreviewDialog,
      handleConfirmAutoFillDialog,
      handleCancelAutoFill,
      handleOpenTour,
      handleCloseTour,
      handleRestartTour,
      refreshTourResumeStep,
      openGuide,
      currentTourStepId,
      runtimeIntent,
      effectiveAutoFillDialogOpen,
      autoFillPreviewOnly,
      effectiveAutoFillDialogMode,
      effectiveAutoFillDialogPreview,
      effectiveFindReplaceOpen,
      findReplacePreviewOnly,
    }),
    [
      parameterChangeDialog,
      autoFillDialog,
      findReplaceOpen,
      csvImportOpen,
      guideOpen,
      tourOpen,
      tourStep,
      setTourStep,
      tourResumeStep,
      handleConfirmParameterChange,
      handleCancelParameterChange,
      handleAutoFillPreviewDialog,
      handleConfirmAutoFillDialog,
      handleCancelAutoFill,
      handleOpenTour,
      handleCloseTour,
      handleRestartTour,
      refreshTourResumeStep,
      openGuide,
      currentTourStepId,
      runtimeIntent,
      effectiveAutoFillDialogOpen,
      autoFillPreviewOnly,
      effectiveAutoFillDialogMode,
      effectiveAutoFillDialogPreview,
      effectiveFindReplaceOpen,
      findReplacePreviewOnly,
    ]
  );
}
